import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Search, Edit, Trash2, Shield, X,
  Mail, UserPlus, Upload, Check, ChevronDown, Filter,
  Briefcase, UserCheck, Building2
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

// All platform roles including new Consulting Suite roles
const ALL_ROLES = [
  // Core Platform Roles
  { key: 'sysadmin', name: 'System Admin', category: 'Platform', description: 'Full system access' },
  { key: 'pmo_power', name: 'PMO Power User', category: 'Platform', description: 'Portfolio & program management' },
  { key: 'program_admin', name: 'Program Admin', category: 'Platform', description: 'Program-level administration' },
  { key: 'pm', name: 'Project Manager', category: 'Platform', description: 'Project management access' },
  { key: 'viewer', name: 'Viewer', category: 'Platform', description: 'Read-only access' },
  
  // Consulting Suite Roles
  { key: 'consulting_admin', name: 'Consulting Suite Admin', category: 'Consulting Suite', description: 'Full control of Consulting Suite features', badge: 'New' },
  { key: 'consultant', name: 'Consultant', category: 'Consulting Suite', description: 'Access to proposal and planning tools' },
  { key: 'proposal_author', name: 'Proposal Author', category: 'Consulting Suite', description: 'Create and edit proposals' },
  { key: 'solution_architect', name: 'Solution Architect', category: 'Consulting Suite', description: 'Technical proposal design' },
  { key: 'engagement_manager', name: 'Engagement Manager', category: 'Consulting Suite', description: 'Manage client engagements' },
  { key: 'rfp_lead', name: 'RFP Lead', category: 'Consulting Suite', description: 'Lead RFP response efforts' },
  { key: 'delivery_lead', name: 'Delivery Lead', category: 'Consulting Suite', description: 'Oversee project delivery' },
  
  // Content & Partner Roles
  { key: 'content_manager', name: 'Content Manager', category: 'Content', description: 'Manage templates and documentation' },
  { key: 'partner_manager', name: 'Partner Manager', category: 'Partner', description: 'Manage partner relationships' }
];

const ROLE_CATEGORIES = ['All', 'Platform', 'Consulting Suite', 'Content', 'Partner'];

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(ALL_ROLES);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [newUser, setNewUser] = useState({ 
    email: '', 
    name: '', 
    roles: ['viewer'], 
    scope_type: 'global',
    persona: ''
  });

  // Get current user's role and tenant from localStorage
  const currentUserRole = localStorage.getItem('simpliview_user_role');
  const currentUserTenantId = localStorage.getItem('simpliview_user_tenant_id');
  const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
  
  // For non-sysadmin users, always filter by their tenant
  // For sysadmin users impersonating a tenant, filter by the impersonated tenant
  const isSysadmin = currentUserRole === 'sysadmin';
  const activeTenantId = isSysadmin ? impersonatedTenantId : currentUserTenantId;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Build params - include tenant_id for tenant-scoped views
      const params = { search: searchQuery };
      if (activeTenantId && !isSysadmin) {
        // Non-sysadmin users always see only their tenant's users
        params.tenant_id = activeTenantId;
      } else if (activeTenantId && isSysadmin) {
        // Sysadmin impersonating a tenant - still show all users but could filter if desired
        // For now, sysadmins see all users (can add toggle later if needed)
      }
      
      const [usersRes, rolesRes] = await Promise.all([
        api.getAdminUsers(params),
        api.getAdminRoles()
      ]);
      setUsers(usersRes.data.users || []);
      // Merge API roles with our predefined roles
      const apiRoles = rolesRes.data.roles || [];
      const mergedRoles = [...ALL_ROLES];
      apiRoles.forEach(r => {
        if (!mergedRoles.find(mr => mr.key === r.key)) {
          mergedRoles.push({ ...r, category: 'Platform' });
        }
      });
      setRoles(mergedRoles);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const [createdUserCredentials, setCreatedUserCredentials] = useState(null);

  const handleCreateUser = async () => {
    if (!newUser.email) {
      toast.error('Email is required');
      return;
    }
    try {
      // Include the active tenant_id when creating users as a tenant admin
      const userData = {
        ...newUser,
        role: newUser.roles[0] // Primary role for backward compatibility
      };
      
      // For tenant admins, ensure the user is created in their tenant
      if (activeTenantId && !isSysadmin) {
        userData.tenant_id = activeTenantId;
      }
      
      const response = await api.createAdminUser(userData);
      
      // If credentials are returned (email not configured), show them to admin
      if (response.data.credentials) {
        setCreatedUserCredentials(response.data.credentials);
      }
      
      setShowCreateModal(false);
      setNewUser({ email: '', name: '', roles: ['viewer'], scope_type: 'global', persona: '' });
      loadData();
      
      if (response.data.email_sent) {
        toast.success('User invited successfully. Welcome email sent.');
      } else {
        toast.success('User created. Please share the login credentials with them.');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.error === 'seat_limit_reached') {
        toast.error(`Seat limit reached. ${errorDetail.current_users}/${errorDetail.seat_limit} seats used. Please upgrade your plan.`);
      } else {
        toast.error('Failed to create user: ' + (errorDetail?.message || error.message));
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.deleteAdminUser(userId);
      loadData();
      toast.success('User deleted');
    } catch (error) {
      toast.error('Failed to delete user: ' + error.message);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await api.updateAdminUser(userId, { is_active: !isActive });
      loadData();
      toast.success(isActive ? 'User deactivated' : 'User activated');
    } catch (error) {
      toast.error('Failed to update user: ' + error.message);
    }
  };

  const handleUpdateUserRoles = async (userId, newRoles) => {
    try {
      await api.updateAdminUser(userId, { roles: newRoles });
      loadData();
      toast.success('Roles updated');
    } catch (error) {
      toast.error('Failed to update roles: ' + error.message);
    }
  };

  const getRoleColor = (roleKey) => {
    const colors = {
      'sysadmin': 'bg-red-100 text-red-700',
      'pmo_power': 'bg-purple-100 text-purple-700',
      'program_admin': 'bg-blue-100 text-blue-700',
      'pm': 'bg-green-100 text-green-700',
      'viewer': 'bg-gray-100 text-gray-700',
      // Consulting Suite roles
      'consulting_admin': 'bg-purple-100 text-purple-700',
      'consultant': 'bg-indigo-100 text-indigo-700',
      'proposal_author': 'bg-cyan-100 text-cyan-700',
      'solution_architect': 'bg-teal-100 text-teal-700',
      'engagement_manager': 'bg-orange-100 text-orange-700',
      'rfp_lead': 'bg-amber-100 text-amber-700',
      'delivery_lead': 'bg-emerald-100 text-emerald-700',
      // Other roles
      'content_manager': 'bg-pink-100 text-pink-700',
      'partner_manager': 'bg-blue-100 text-blue-700'
    };
    return colors[roleKey] || 'bg-gray-100 text-gray-700';
  };

  const filteredUsers = users.filter(user => {
    if (roleFilter === 'All') return true;
    return user.roles?.some(r => {
      const roleInfo = roles.find(role => role.key === r.role_key);
      return roleInfo?.category === roleFilter;
    });
  });

  const filteredRoles = roleFilter === 'All' 
    ? roles 
    : roles.filter(r => r.category === roleFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Users className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            User Management
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Manage users, roles, and access permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)]"
          data-testid="invite-user-btn"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadData()}
              className="pl-10 pr-4 py-2 w-full border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              data-testid="user-search"
            />
          </div>
          
          {/* Role Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[var(--clr-text-muted)]" />
            <div className="flex gap-1">
              {ROLE_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setRoleFilter(cat)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    roleFilter === cat
                      ? 'bg-[var(--clr-brand-primary)] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={loadData}
            className="px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium"
          >
            Search
          </button>
        </div>
      </div>

      {/* Role Legend */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
        <h3 className="text-sm font-medium text-[var(--clr-text-secondary)] mb-3">Available Roles</h3>
        <div className="flex flex-wrap gap-2">
          {filteredRoles.map((role) => (
            <div
              key={role.key}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${getRoleColor(role.key)}`}
              title={role.description}
            >
              {role.category === 'Consulting Suite' && <Briefcase className="w-3 h-3" />}
              {role.name}
              {role.badge && (
                <span className="px-1.5 py-0.5 bg-white/50 rounded text-[10px]">{role.badge}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[var(--clr-surface-subtle)] border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Roles</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Persona</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p>No users found</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-[var(--clr-brand-primary)] font-semibold">
                            {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[var(--clr-text-primary)]">{user.name}</p>
                          <p className="text-sm text-[var(--clr-text-muted)]">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {user.roles?.slice(0, 3).map((role, idx) => (
                          <span 
                            key={idx}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(role.role_key)}`}
                          >
                            {roles.find(r => r.key === role.role_key)?.name || role.role_name}
                          </span>
                        ))}
                        {user.roles?.length > 3 && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            +{user.roles.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[var(--clr-text-secondary)]">
                        {user.persona || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-700' : 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-[var(--clr-sev-healthy)]' : 'bg-gray-400'}`}></span>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user.id, user.is_active)}
                          className={`p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg ${user.is_active ? 'text-[var(--clr-sev-atrisk)]' : 'text-[var(--clr-sev-healthy)]'}`}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {user.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 hover:bg-red-100 rounded-lg text-[var(--clr-sev-blocked)]"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[var(--clr-brand-primary)]" />
              Invite User
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                  placeholder="user@company.com"
                  data-testid="new-user-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                  placeholder="John Doe"
                  data-testid="new-user-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="space-y-3">
                  {ROLE_CATEGORIES.filter(c => c !== 'All').map((category) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-gray-500 mb-1">{category}</p>
                      <div className="flex flex-wrap gap-2">
                        {roles.filter(r => r.category === category).map((role) => (
                          <label
                            key={role.key}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                              newUser.roles.includes(role.key)
                                ? 'bg-purple-50 border-purple-300'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={newUser.roles.includes(role.key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewUser({ ...newUser, roles: [...newUser.roles, role.key] });
                                } else {
                                  setNewUser({ ...newUser, roles: newUser.roles.filter(r => r !== role.key) });
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-purple-600"
                            />
                            <span className="text-sm">{role.name}</span>
                            {role.badge && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-700 rounded">{role.badge}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Persona</label>
                <select
                  value={newUser.persona}
                  onChange={(e) => setNewUser({ ...newUser, persona: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                >
                  <option value="">None</option>
                  <option value="consulting_teams">Consulting Teams</option>
                  <option value="pmos">PMOs</option>
                  <option value="partners">Partners</option>
                  <option value="product_teams">Product Teams</option>
                  <option value="rfp_response_teams">RFP Response Teams</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                <select
                  value={newUser.scope_type}
                  onChange={(e) => setNewUser({ ...newUser, scope_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                >
                  <option value="global">Global (All Data)</option>
                  <option value="portfolio">Portfolio-specific</option>
                  <option value="program">Program-specific</option>
                  <option value="project">Project-specific</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={!newUser.email || newUser.roles.length === 0}
                className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                data-testid="create-user-submit"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedUser(null)}></div>
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">Edit User</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={selectedUser.email}
                  disabled
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-[var(--clr-surface-subtle)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Roles</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                  {selectedUser.roles?.map((role, idx) => (
                    <span key={idx} className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getRoleColor(role.role_key)}`}>
                      {roles.find(r => r.key === role.role_key)?.name || role.role_name}
                      <button
                        onClick={() => {
                          const newRoles = selectedUser.roles.filter((_, i) => i !== idx);
                          setSelectedUser({ ...selectedUser, roles: newRoles });
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Role</label>
                <select
                  onChange={(e) => {
                    if (e.target.value && !selectedUser.roles?.find(r => r.role_key === e.target.value)) {
                      const roleInfo = roles.find(r => r.key === e.target.value);
                      setSelectedUser({
                        ...selectedUser,
                        roles: [...(selectedUser.roles || []), { role_key: e.target.value, role_name: roleInfo?.name }]
                      });
                    }
                    e.target.value = '';
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select role to add...</option>
                  {roles.map((role) => (
                    <option 
                      key={role.key} 
                      value={role.key}
                      disabled={selectedUser.roles?.find(r => r.role_key === role.key)}
                    >
                      {role.name} ({role.category})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Persona</label>
                <select
                  value={selectedUser.persona || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, persona: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  <option value="consulting_teams">Consulting Teams</option>
                  <option value="pmos">PMOs</option>
                  <option value="partners">Partners</option>
                  <option value="product_teams">Product Teams</option>
                  <option value="rfp_response_teams">RFP Response Teams</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await api.updateAdminUser(selectedUser.id, { 
                    name: selectedUser.name,
                    roles: selectedUser.roles?.map(r => r.role_key),
                    persona: selectedUser.persona
                  });
                  setSelectedUser(null);
                  loadData();
                  toast.success('User updated');
                }}
                className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--clr-brand-primary-hover)]"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal - Shows after creating a user when email is not configured */}
      {createdUserCredentials && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--clr-text-primary)]">User Created Successfully</h3>
                <p className="text-sm text-[var(--clr-text-muted)]">Share these credentials with the new user</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                ⚠️ Email notifications are not configured. Please share these credentials securely.
              </p>
            </div>

            <div className="space-y-3">
              <div className="bg-[var(--clr-surface-base)] rounded-lg p-3">
                <label className="block text-xs text-[var(--clr-text-muted)] mb-1">Email</label>
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-[var(--clr-text-primary)]">{createdUserCredentials.email}</code>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(createdUserCredentials.email);
                      toast.success('Email copied');
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-[var(--clr-surface-base)] rounded-lg p-3">
                <label className="block text-xs text-[var(--clr-text-muted)] mb-1">Temporary Password</label>
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-[var(--clr-text-primary)]">{createdUserCredentials.temp_password}</code>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(createdUserCredentials.temp_password);
                      toast.success('Password copied');
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-[var(--clr-surface-base)] rounded-lg p-3">
                <label className="block text-xs text-[var(--clr-text-muted)] mb-1">Login URL</label>
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-[var(--clr-text-primary)] truncate">{createdUserCredentials.login_url}</code>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(createdUserCredentials.login_url);
                      toast.success('URL copied');
                    }}
                    className="text-xs text-blue-600 hover:underline ml-2"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-[var(--clr-text-muted)] mt-3">
              The user will be required to change their password on first login.
            </p>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  const text = `Your ScopeOps account has been created.\n\nEmail: ${createdUserCredentials.email}\nTemporary Password: ${createdUserCredentials.temp_password}\nLogin URL: ${createdUserCredentials.login_url}\n\nPlease change your password after logging in.`;
                  navigator.clipboard.writeText(text);
                  toast.success('All credentials copied to clipboard');
                }}
                className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-[var(--clr-surface-hover)]"
              >
                Copy All
              </button>
              <button
                onClick={() => setCreatedUserCredentials(null)}
                className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--clr-brand-primary-hover)]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
